import { CheckCircleFill } from "@material-symbols-svg/react/rounded/icons/check-circle";
import { Error as ErrorIcon } from "@material-symbols-svg/react/rounded/icons/error";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";
import { PASSWORD_CRITERIA } from "./password-criteria";

type PasswordCriteriaChecklistProps = {
  /** DOM id so the password input can reference the list via aria-describedby. */
  id: string;
  password: string;
  /** After a submit attempt unmet criteria switch from neutral to error red. */
  attempted: boolean;
};

/** Live password-rule checklist; it is the validation display per design. */
export function PasswordCriteriaChecklist({
  id,
  password,
  attempted,
}: PasswordCriteriaChecklistProps) {
  const { t } = useTranslation("auth");

  return (
    <ul id={id} className="flex flex-col gap-2">
      {PASSWORD_CRITERIA.map((criterion) => {
        const met = criterion.test(password);
        return (
          <li
            key={criterion.key}
            data-met={met}
            className={cn(
              "flex items-center gap-2 text-label-small-prominent",
              met && "text-(--extended-colors-success)",
              !met &&
                (attempted ? "text-destructive" : "text-muted-foreground"),
            )}
          >
            {met ? (
              <CheckCircleFill aria-hidden className="size-5 shrink-0" />
            ) : (
              <ErrorIcon aria-hidden className="size-5 shrink-0" />
            )}
            {/* The icons are aria-hidden, so the met state must also exist as
                text for screen readers (WCAG 1.3.1). */}
            <span className="sr-only">
              {met
                ? t("passwordCriteria.state.met")
                : t("passwordCriteria.state.notMet")}
              {": "}
            </span>
            {t(`passwordCriteria.${criterion.key}`)}
          </li>
        );
      })}
    </ul>
  );
}
